import type { PerformanceProFeatureValue } from '@/constants/PerformanceProFeature';
import type { PermissionValue } from '@/constants/Permission';
import type { ReactNode } from 'react';

import { useProGate } from '@/hooks/performance-tier/useProGate';
import { ProGateOutcome } from '@/utils/performanceTier';
import { requestPerformanceUpgrade } from '@/utils/requestPerformanceUpgrade';

interface ProGateProps {
    /** The feature behind the access point; named in the upgrade pop-up when it is locked. */
    feature: PerformanceProFeatureValue;
    /** The Performance permission the feature needs, when the surrounding screen does not imply it. */
    permission?: PermissionValue;
    /**
     * What the access point looks like locked. Each surface draws its own lock (a tile, a menu
     * option, a section), so the gate only hands it the action that offers the upgrade.
     */
    renderLocked: (openUpgrade: () => void) => ReactNode;
    children: ReactNode;
}

/**
 * Shows, locks or hides an access point to a Performance PRO feature (see `resolveProGate`).
 *
 * The sibling of `PermissionGuard`, not a change to it: that guard redirects, and a locked feature
 * has to stay where it is so the upgrade offer has something to attach to.
 */
export const ProGate = ({ feature, permission, renderLocked, children }: ProGateProps) => {
    const outcome = useProGate(permission);

    if (outcome === ProGateOutcome.Show) {
        return <>{children}</>;
    }

    if (outcome === ProGateOutcome.Lock) {
        return <>{renderLocked(() => requestPerformanceUpgrade(feature))}</>;
    }

    return null;
};
